import { ReactNode, useEffect } from "react"
import {
   TableCell as MuiTableCell,
   TableRow as MuiTableRow,
} from "@mui/material"
import classNames from "classnames"
import { Actions } from "./Actions"
import { ActionsBulk } from "./ActionsBulk"
import { Cell } from "./Cell"
import { ExtraActions } from "./ExtraActions"
import { Column } from "./types"
import Spinner, { SpinnerSize } from "@/components/shared/Spinner"
import useFormStateHandler from "@/hooks/useFormStateHandler"

export interface RowProps<T> {
   row: T
   draggable?: boolean
   columns: Column<T>[]
   isDisabled: boolean
   isDimmed?: boolean
   isSaveDisabled?: boolean
   isEditDisabled?: boolean
   isEditing: boolean
   editable?: boolean
   validationErrors?: Record<string, boolean>
   extraActions?: ReactNode
   extraFields?: Partial<T>
   loading: boolean
   error: string | null
   isNewRow: boolean
   isFirstEditableRow: boolean
   isBulkCreating: boolean
   onEdit: (id: string) => void
   onBulkSave: () => void
   onSave: (form: T) => void
   onCancel: () => void
   onBulkCancel: () => void
   onDelete: (id: string) => void
   onBulkItemDelete?: (id: string) => void
   onInputChange: (field: keyof T, value: string) => void
   onPasted?: (rows: string[], column: Column<T>) => void
}

export function Row<T extends { id: string; draggable?: boolean }>({
   row,
   draggable = true,
   columns,
   isDisabled,
   isDimmed = false,
   isSaveDisabled = false,
   isEditDisabled = false,
   isEditing,
   editable = true,
   validationErrors = {},
   extraActions,
   extraFields,
   loading,
   error,
   isNewRow,
   isFirstEditableRow,
   isBulkCreating,
   onEdit,
   onBulkSave,
   onSave,
   onCancel,
   onBulkCancel,
   onDelete,
   onBulkItemDelete = () => {},
   onInputChange,
   onPasted,
}: RowProps<T>) {
   const hasActions = editable
   const [rowFormState, setState] = useFormStateHandler<T>(row)

   useEffect(() => {
      setState(row)
   }, [row, setState])

   useEffect(() => {
      if (extraFields) {
         setState((prevState) => ({
            ...prevState,
            ...extraFields,
         }))
      }
   }, [extraFields, setState])

   const handlePaste = (rows: string[], column: Column<T>) => {
      if (!isNewRow) {
         return
      }

      if (onPasted) {
         onPasted(rows, column)
      }
   }

   return (
      <>
         {loading ? (
            <MuiTableRow className={classNames("table__row")}>
               <MuiTableCell
                  className={classNames("table__cell", {
                     "table__cell--loading": loading,
                  })}
                  colSpan={editable ? columns.length + 1 : columns.length}
               >
                  <Spinner size={SpinnerSize.SMALL} />
               </MuiTableCell>
            </MuiTableRow>
         ) : error ? (
            <MuiTableRow className={classNames("table__row")}>
               <MuiTableCell
                  className={classNames("table__cell", {
                     "table__cell--error": error,
                  })}
                  colSpan={editable ? columns.length + 1 : columns.length}
               >
                  {error}
               </MuiTableCell>
            </MuiTableRow>
         ) : (
            <MuiTableRow
               className={classNames("table__row", {
                  "table__row--editing": isEditing,
                  "table__row--not-draggable": !draggable,
               })}
            >
               {columns.map((column) => {
                  const value = rowFormState[column.key]

                  return (
                     <Cell<T>
                        id={String(column.key)}
                        key={String(column.key)}
                        column={column}
                        row={row}
                        isEditing={isEditing}
                        isDimmed={isDimmed}
                        onInputChange={(field, value) => {
                           onInputChange(field, value)
                           setState({
                              ...rowFormState,
                              [field]: value,
                           })
                        }}
                        onBulkPaste={(rows) => handlePaste(rows, column)}
                        value={value}
                        inputValue={value}
                        hasError={
                           isEditing && validationErrors[column.key as string]
                        }
                     />
                  )
               })}
               {hasActions && (
                  <>
                     {!isBulkCreating ? (
                        <Actions
                           hideDelete={isNewRow}
                           isDisabled={isDisabled}
                           isSaveDisabled={isSaveDisabled}
                           isEditDisabled={isEditDisabled}
                           isEditing={isEditing}
                           onEdit={() => onEdit(row.id)}
                           onSave={() => onSave(rowFormState)}
                           onCancel={() => {
                              setState(row)
                              onCancel()
                           }}
                           onDelete={() => onDelete(row.id)}
                        />
                     ) : (
                        <ActionsBulk
                           isDisabled={isDisabled}
                           isSaveDisabled={isSaveDisabled}
                           isFirstEditableRow={isFirstEditableRow}
                           onSave={onBulkSave}
                           onCancel={onBulkCancel}
                           onDelete={() => onBulkItemDelete(row.id)}
                        />
                     )}
                     {extraActions && (
                        <ExtraActions
                           isDisabled={isDisabled}
                           extraActions={extraActions}
                        />
                     )}
                  </>
               )}
            </MuiTableRow>
         )}
      </>
   )
}
